import React, { createContext, useContext, ReactNode, useState } from "react";
import { StayDataType, IntegrationType } from "data/types"; // Import the listing type
import { addDays } from "date-fns";
import { CLIENT_LISTINGS_OBJECT } from "data/backEndTypes";

// Define the structure for the color shades
interface ColorShades {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

// Define the structure for the colorsTheme
interface ColorsTheme {
  PRIMARY_COLOR: ColorShades;
  SECONDARY_COLOR: ColorShades;
  NEUTRAL_COLOR: ColorShades;
}

interface lodgifySiteType {
  id: string;
  url: string;
}
interface wixCmsApiType {
  cmsRequest: boolean;
  cmsURL?: string;
}

interface GlobalSearchType {
  isLoading: boolean;
  isSearching?: boolean; // use to trigger react-query to search
  params: {
    checkIn: Date | null; //null so the dates can be cleared
    checkOut: Date | null; //null so the dates can be cleared
    guestsCount: number;
    location?: LocationsType;
  };
}

interface LocationsType {
  city: string;
  country?: string;
  state?: string;
}

type ReqType =
  | "ALL_LISTINGS"
  | "SINGLE_LISTING"
  | "PAYMENT_PAGE"
  | "SEARCH_LISTINGS"
  | "ALL_CALENDAR_LISTINGS";

interface ActionsProps {
  fetchListings: string;
  fetchListingDetails: string;
  fetchAvailabilities: string;
  fetchListingSearch: string;
  fetchListingQuote: string;
  fetchListingNextPage?: string;
  fetchListingReservation?: string;
  fetchListingCurrencies?: string; // logdify only for now 
  fetchListingPaymentPage?: string // guesty and hostaway
  fetchLocations?: string; 
  fetchRateCalendar: string;
}
interface TotalListingsType {
  total: number;
  pagination?: string;
  prevPagination?: string;
}
export interface GlobalCurrencyType {
  id: number;
  code: string;
  name: string;
  euro_forex: number;
  symbol: string;
  decimal_places: number;
}

// Define the ClientContext type with state and setters
interface ClientContextType {
  listingsPage: listingsPageType;
  wix_cms_api: wixCmsApiType;
  global_searh: GlobalSearchType;
  global_language: string;
  global_currency: string[];
  global_currency_details: GlobalCurrencyType;
  Locations: Array<LocationsType>;
  totalListings: TotalListingsType;
  listings: StayDataType[];
  filteredListings: StayDataType[];
  internal_ID: string;
  isLocationFilter: boolean;
  clientName: string;
  heroImg: string;
  integration_TYPE: IntegrationType;
  colorsTheme: ColorsTheme;
  actions: ActionsProps;
  requestType: ReqType;
  isListingsLoading: boolean;
  noListingsFound: boolean;
  global_max_guests: number;
  lodgifySite: lodgifySiteType;
  referralId?: string | null;



  // Setters
  setListingsPage: React.Dispatch<React.SetStateAction<listingsPageType>>
  setWix_cms_api: React.Dispatch<React.SetStateAction<wixCmsApiType>>;
  setGlobal_search: React.Dispatch<React.SetStateAction<GlobalSearchType>>;
  setGlobal_language: React.Dispatch<React.SetStateAction<string>>;
  setGlobal_currency: React.Dispatch<React.SetStateAction<string[]>>;
  setGlobal_currency_details: React.Dispatch<
    React.SetStateAction<GlobalCurrencyType>
  >;
  setTotalListings: (value: any) => void;
  setLocations: (value: Array<LocationsType>) => void;
  setListings: React.Dispatch<React.SetStateAction<StayDataType[]>>;
  setFilteredListings: (filteredListings: StayDataType[]) => void;
  setInternal_ID: (id: string) => void;
  setIntegration_TYPE: React.Dispatch<React.SetStateAction<IntegrationType>>;
  setColorsTheme: (theme: ColorsTheme) => void;
  setActions: (actions: ActionsProps) => void;
  setIsLocationFilter: (value: boolean) => void;
  setHeroImg: (value: string) => void;
  setClientName: (value: string) => void;
  setRequestType: React.Dispatch<React.SetStateAction<ReqType>>;
  setIsListingsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setNoListingsFound: React.Dispatch<React.SetStateAction<boolean>>;
  setGlobal_max_guests: React.Dispatch<React.SetStateAction<number>>;
  setLodgifySite: React.Dispatch<React.SetStateAction<lodgifySiteType>>;
  setReferralId: React.Dispatch<React.SetStateAction<string | null | undefined>>;
}
interface listingsPageType {
  listings: CLIENT_LISTINGS_OBJECT[];
  searchResults:CLIENT_LISTINGS_OBJECT[];
  totalListings: number;
  totalListingFound?: number | 0;
  currentPage: number;
  totalListingPerPage: number;
  pagination?:string;
  nextPageUrl?:string;
  previousPageUrl?:string;

}
// Create the ClientContext
const ClientContext = createContext<ClientContextType | undefined>(undefined);

interface ClientProviderProps {
  children: ReactNode;
}

// Move getActions outside component to avoid redeclaration on every render
export const getActions = (type: IntegrationType): ActionsProps => {
  if (type === "guesty") {
    return {
      fetchRateCalendar: "",
      fetchLocations: "getGuesty_ListingLocations",
      fetchListings: "getGuesty_Listings",
      fetchListingDetails: "getGuesty_ListingDetails",
      fetchAvailabilities: "getGuesty_ListingAvailabilities",
      fetchListingSearch: "getGuesty_ListingsSearch",
      fetchListingNextPage: "getGuesty_NextPage",
      fetchListingQuote: "getGuesty_ListingQuote",
      fetchListingReservation: "getGuesty_ListingReservation",
      fetchListingPaymentPage: "getGuesty_PaymentPage"

    };
  } else if (type === "lodgify") {
    return {
      fetchRateCalendar: "getLodgify_ListingsRateCalendar",
      fetchLocations: "getLodgify_ListingLocations",
      fetchListings: "getLodgify_Listings",
      fetchListingDetails: "getLodgify_ListingDetails",
      fetchAvailabilities: "getLodgify_ListingCalendar",
      fetchListingSearch: "getLodgify_Listings_search",
      fetchListingQuote: "getLodgify_ListingApiQuote",//"getLodgify_ListingQuote",
      fetchListingCurrencies: "getLodgify_ListingCurrencies"
    };
  } else if (type === "hostaway") {
  }
  return {
    fetchRateCalendar: "",
    fetchLocations: "getHostaway_Locations",
    fetchListings: "getHostaway_Listings",
    fetchListingDetails: "getHostaway_LisingDetails",
    fetchAvailabilities: "",
    fetchListingSearch: "getHostaway_ListingsSearch",
    fetchListingQuote: "getHostaway_ListingQuote",
    fetchListingNextPage: "",
    fetchListingReservation: "getHostaway_ListingReservation",
    fetchListingPaymentPage: "getHostaway_PaymentPage"
  };
};

//const getCurrencyDetails = (code: string) => {
// switch (code) {
//   case "USD":
//     return { id: code, name: "USD", href: "##",active:false };
//   case "CAD":
//     return { code: code, name: "CAD",  href: "##" ,active:false};
//  case "EUR":
//    return { code: code, name: "EUR", href: "##",active:false};
//  default:
//    return { code: "USD", name: "USD", href: "##",active:true };
// }
//};

const DEFAULT_DATE_RANGE_START = new Date();
const DEFAULT_DATE_RANGE_END = addDays(DEFAULT_DATE_RANGE_START, 5);

// ClientProvider Component with internal state management
export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const [wix_cms_api, setWix_cms_api] = useState<wixCmsApiType>({
    cmsRequest: false,
    cmsURL: "",
  });
  const [requestType, setRequestType] = useState<ReqType>("ALL_LISTINGS");
  const [lodgifySite, setLodgifySite] = useState<lodgifySiteType>({
    id: "",
    url: "",
  });
  const [referralId, setReferralId] = useState<string | null>();
  const [global_language, setGlobal_language] = useState("en");
  const [global_currency, setGlobal_currency] = useState(["USD"]);
  const [global_currency_details, setGlobal_currency_details] = useState({
    id: 50,
    code: "USD",
    name: "US dollar",
    euro_forex: 1.0485,
    symbol: "$  ",
    decimal_places: 2,
  });
  const [global_max_guests, setGlobal_max_guests] = useState(1);
  const [global_searh, setGlobal_search] = useState<GlobalSearchType>({
    isLoading: false,
    isSearching: false,
    params: {
      checkIn: DEFAULT_DATE_RANGE_START,
      checkOut: DEFAULT_DATE_RANGE_END,
      guestsCount: 1,
      location: {
        city: "",
        state: "",
        country: "",
      },
    },
  });
  const [Locations, setLocations] = useState<Array<LocationsType>>([]);
  const [totalListings, setTotalListings] = useState<TotalListingsType>({
    total: 0,
    pagination: "",
    prevPagination: "",
  });
  const [listingsPage, setListingsPage] = useState<listingsPageType>({
    listings: [] as CLIENT_LISTINGS_OBJECT[],
    searchResults: [] as CLIENT_LISTINGS_OBJECT[],
    totalListings: 0,
    totalListingFound: 0,
    currentPage: 0,
    totalListingPerPage: 9,
    pagination: "",
    nextPageUrl:"",
    previousPageUrl:""
  });
  const [listings, setListings] = useState<StayDataType[]>([]); //should be hook
  const [isListingsLoading, setIsListingsLoading] = useState(false); //should be hook
  const [noListingsFound, setNoListingsFound] = useState(false); //should be hook
  const [filteredListings, setFilteredListings] = useState<StayDataType[]>([]); //should be hook
  const [internal_ID, setInternal_ID] = useState("n/a");
  const [isLocationFilter, setIsLocationFilter] = useState(false);
  const [heroImg, setHeroImg] = useState("");
  const [clientName, setClientName] = useState("n/a");
  const [integration_TYPE, setIntegration_TYPE] = useState<IntegrationType>("");
  const [actions, setActions] = useState<ActionsProps>({
    fetchListings: "",
    fetchListingDetails: "",
    fetchAvailabilities: "",
    fetchListingSearch: "",
    fetchListingQuote: "",
    fetchListingNextPage: "",
    fetchRateCalendar: "",
  });

  const defaultColorsTheme: ColorsTheme = {
    PRIMARY_COLOR: {
      50: "48, 174, 181",
      100: "48, 174, 181",
      200: "48, 174, 181",
      300: "48, 174, 181",
      400: "48, 174, 181",
      500: "48, 174, 181",
      600: "48, 174, 181",
      700: "48, 174, 181",
      800: "48, 174, 181",
      900: "48, 174, 181",
    },
    SECONDARY_COLOR: {
      50: "#FFF7ED",
      100: "#FFEDD5",
      200: "#FED7AA",
      300: "#FDBA74",
      400: "#FB923C",
      500: "#F97316",
      600: "#EA580C",
      700: "#C2410C",
      800: "#9A3412",
      900: "#7C2D12",
    },
    NEUTRAL_COLOR: {
      50: "#F8FAFC",
      100: "#F1F5F9",
      200: "#E2E8F0",
      300: "#CBD5E1",
      400: "#94A3B8",
      500: "#64748B",
      600: "#475569",
      700: "#334155",
      800: "#1E293B",
      900: "#0F172A",
    },
  };

  const [colorsTheme, setColorsTheme] =
    useState<ColorsTheme>(defaultColorsTheme);
  //console.log("from state",global_searh)
  return (
    <ClientContext.Provider
      value={{
        listingsPage,
        wix_cms_api,
        requestType,
        global_searh,
        global_language,
        global_currency,
        global_currency_details,
        totalListings,
        Locations,
        listings,
        filteredListings,
        internal_ID,
        isLocationFilter,
        clientName,
        heroImg,
        integration_TYPE,
        colorsTheme,
        actions,
        isListingsLoading,
        noListingsFound,
        global_max_guests,
        lodgifySite,
        referralId,
        setListingsPage,
        setWix_cms_api,
        setRequestType,
        setGlobal_search,
        setGlobal_language,
        setGlobal_currency,
        setGlobal_currency_details,
        setTotalListings,
        setLocations,
        setListings,
        setFilteredListings,
        setInternal_ID,
        setIntegration_TYPE,
        setColorsTheme,
        setActions,
        setIsLocationFilter,
        setHeroImg,
        setClientName,
        setIsListingsLoading,
        setNoListingsFound,
        setGlobal_max_guests,
        setLodgifySite,
        setReferralId,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

// Custom hook to access the ClientContext
export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClient must be used within a ClientProvider");
  }
  return context;
};
